import { useEffect, useState, useCallback } from "react";
import {
  Table, Button, Typography, Tag, Tabs, Select, Input, Modal, Form, Switch, Alert, AutoComplete, Tooltip, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { api } from "../../services/api";

const { Text } = Typography;
const PRIMARY = "#4f46e5";

const etiqueta = (l) => (l.departamento && l.departamento !== l.nombre ? `${l.nombre} (${l.departamento})` : l.nombre);

// Localidades: lo que los clientes escribieron a mano porque no estaba en la
// lista, para sumarlo o atarlo a la correcta, y la lista entera para mantenerla.
export const Localidades = () => {
  const [provincias, setProvincias] = useState([]);

  useEffect(() => {
    api.platform.geo.provincias().then((res) => {
      if (res.status === "success") setProvincias(res.data || []);
    });
  }, []);

  return (
    <div>
      <Text strong style={{ fontSize: 28, lineHeight: 1.2, display: "block", marginBottom: 4 }}>Localidades</Text>
      <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
        Las oficiales vienen de Georef (INDEC). Las que faltan se cargan acá y quedan disponibles para todas las tiendas.
      </Text>
      <Tabs
        items={[
          { key: "escritas", label: "Escritas por clientes", children: <Escritas provincias={provincias} /> },
          { key: "lista", label: "Lista", children: <Lista provincias={provincias} /> },
        ]}
      />
    </div>
  );
};

// ── Escritas por clientes ─────────────────────────────────────────────────

const Escritas = () => {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolviendo, setResolviendo] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.platform.geo.escritas();
      if (res.status === "success") setFilas(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const columns = [
    { title: "Lo que escribieron", dataIndex: "texto", key: "texto", render: (v) => <Text strong>{v}</Text> },
    { title: "Provincia", dataIndex: "provincia", key: "provincia" },
    {
      title: "Domicilios", key: "uso", width: 140,
      render: (_, f) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {f.domicilios}{f.tiendas > 1 && <Text type="secondary"> en {f.tiendas} tiendas</Text>}
        </span>
      ),
    },
    {
      title: "", key: "acciones", width: 120, align: "right",
      render: (_, f) => <Button size="small" onClick={() => setResolviendo(f)}>Resolver</Button>,
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {loading ? " " : filas.length === 0
            ? "No hay nada pendiente."
            : `${filas.length} sin resolver. Sumalas a la lista o atalas a la que corresponde si es un error de tipeo.`}
        </Text>
        <Tooltip title="Actualizar">
          <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
        </Tooltip>
      </div>
      <Table
        columns={columns}
        dataSource={filas}
        rowKey={(f) => `${f.id_provincia}-${f.texto}`}
        loading={loading}
        pagination={filas.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
        locale={{ emptyText: "Todos los domicilios tienen una localidad de la lista." }}
        style={{ background: "#fff", borderRadius: 8 }}
      />
      <ResolverModal
        escrita={resolviendo}
        onClose={() => setResolviendo(null)}
        onResuelta={() => { setResolviendo(null); cargar(); }}
      />
    </>
  );
};

const ResolverModal = ({ escrita, onClose, onResuelta }) => {
  const [modo, setModo] = useState("existente");
  const [opciones, setOpciones] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [elegida, setElegida] = useState(null);
  const [nombre, setNombre] = useState("");
  const [departamento, setDepartamento] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const buscar = async (prov, q) => {
    const res = await api.platform.geo.localidades({ provincia: prov, q });
    if (res.status === "success") setOpciones(res.data || []);
  };

  // Arranca buscando lo mismo que escribieron: si es un error de tipeo, la
  // correcta suele aparecer con las primeras letras.
  useEffect(() => {
    if (!escrita) return;
    setModo("existente");
    setElegida(null);
    setError("");
    setNombre(escrita.texto);
    setDepartamento("");
    const inicio = escrita.texto.slice(0, 4);
    setBusqueda(inicio);
    buscar(escrita.id_provincia, inicio);
  }, [escrita]); // eslint-disable-line react-hooks/exhaustive-deps

  const guardar = async () => {
    setError("");
    const base = { id_provincia: escrita.id_provincia, texto: escrita.texto };
    let data;
    if (modo === "existente") {
      if (!elegida) { setError("Elegí la localidad de la lista."); return; }
      data = { ...base, id_localidad: elegida.id };
    } else {
      if (!nombre.trim()) { setError("Falta el nombre."); return; }
      data = { ...base, crear: { nombre, departamento } };
    }
    setGuardando(true);
    try {
      const res = await api.platform.geo.resolverEscrita(data);
      if (res.status === "success") {
        message.success(`Listo: ${res.data.domicilios} domicilio${res.data.domicilios === 1 ? "" : "s"} con ${res.data.localidad.nombre}`);
        onResuelta();
      } else {
        setError(res.message || "No se pudo resolver");
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={!!escrita}
      title={escrita ? `“${escrita.texto}” en ${escrita.provincia}` : ""}
      okText={modo === "existente" ? "Atar a esta localidad" : "Agregar a la lista"}
      onOk={guardar}
      onCancel={guardando ? undefined : onClose}
      confirmLoading={guardando}
      okButtonProps={{ style: { background: PRIMARY } }}
      destroyOnHidden
    >
      {escrita && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {escrita.domicilios} domicilio{escrita.domicilios === 1 ? "" : "s"} con este texto van a quedar con la localidad que elijas.
          </Text>
          <Tabs
            activeKey={modo}
            onChange={setModo}
            size="small"
            items={[
              {
                key: "existente", label: "Es una que ya está",
                children: (
                  <AutoComplete
                    style={{ width: "100%" }}
                    value={busqueda}
                    placeholder="Buscá la localidad correcta"
                    onSearch={(q) => { setBusqueda(q); setElegida(null); buscar(escrita.id_provincia, q); }}
                    onSelect={(_, o) => { setBusqueda(o.value); setElegida({ id: o.id }); }}
                    options={opciones.map((l) => ({ value: etiqueta(l), id: l.id, key: l.id }))}
                  />
                ),
              },
              {
                key: "nueva", label: "Agregar a la lista",
                children: (
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ flex: "2 1 200px" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Nombre, como debería verse</Text>
                      <Input value={nombre} onChange={(e) => setNombre(e.target.value)} />
                    </div>
                    <div style={{ flex: "1 1 160px" }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Departamento (opcional)</Text>
                      <Input value={departamento} onChange={(e) => setDepartamento(e.target.value)} />
                    </div>
                  </div>
                ),
              },
            ]}
          />
          {error && <Alert type="error" message={error} />}
        </div>
      )}
    </Modal>
  );
};

// ── Lista ─────────────────────────────────────────────────────────────────

const Lista = ({ provincias }) => {
  const [provincia, setProvincia] = useState(6);
  const [q, setQ] = useState("");
  const [soloManuales, setSoloManuales] = useState(false);
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alta, setAlta] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q, manuales: soloManuales ? "1" : "" };
      if (provincia) params.provincia = provincia;
      const res = await api.platform.geo.localidades(params);
      if (res.status === "success") setFilas(res.data || []);
    } finally {
      setLoading(false);
    }
  }, [provincia, q, soloManuales]);

  useEffect(() => { cargar(); }, [provincia, soloManuales]); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarActiva = async (l, activa) => {
    const res = await api.platform.geo.editarLocalidad(l.id, { activa });
    if (res.status === "success") {
      setFilas((fs) => fs.map((f) => (f.id === l.id ? { ...f, activa } : f)));
    } else {
      message.error(res.message || "No se pudo cambiar");
    }
  };

  const columns = [
    { title: "Localidad", dataIndex: "nombre", key: "nombre", render: (v) => <Text strong>{v}</Text> },
    { title: "Departamento", dataIndex: "departamento", key: "depto", render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Origen", key: "origen", width: 110,
      render: (_, l) => (l.manual ? <Tag color="purple">Cargada a mano</Tag> : <Tag>INDEC</Tag>),
    },
    {
      title: "Domicilios", dataIndex: "domicilios", key: "uso", width: 110,
      render: (v) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{v}</span>,
    },
    {
      title: "Activa", key: "activa", width: 90,
      render: (_, l) => (
        <Tooltip title={l.activa ? "Desactivarla la saca del buscador; los domicilios que la usan no cambian" : "Volver a mostrarla en el buscador"}>
          <Switch size="small" checked={l.activa} onChange={(v) => cambiarActiva(l, v)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          showSearch
          optionFilterProp="label"
          allowClear
          placeholder="Todas las provincias"
          value={provincia}
          onChange={(v) => setProvincia(v || null)}
          options={provincias.map((p) => ({ value: p.id, label: p.nombre }))}
          style={{ width: 240 }}
        />
        <Input
          prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
          placeholder="Buscar localidad"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onPressEnter={cargar}
          allowClear
          style={{ width: 220 }}
        />
        <Button onClick={cargar}>Buscar</Button>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Switch size="small" checked={soloManuales} onChange={setSoloManuales} id="solo-manuales" />
          <label htmlFor="solo-manuales" style={{ fontSize: 13 }}>Solo cargadas a mano</label>
        </span>
        <div style={{ flex: 1 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAlta(true)} style={{ background: PRIMARY }}>
          Agregar localidad
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={filas}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 25, showSizeChanger: false }}
        locale={{ emptyText: "No hay localidades con ese filtro." }}
        footer={filas.length === 200 ? () => <Text type="secondary" style={{ fontSize: 12 }}>Se muestran las primeras 200: afiná la búsqueda.</Text> : undefined}
        style={{ background: "#fff", borderRadius: 8 }}
      />
      <AltaLocalidad
        open={alta}
        provincias={provincias}
        provinciaInicial={provincia}
        onClose={() => setAlta(false)}
        onCreada={() => { setAlta(false); cargar(); }}
      />
    </>
  );
};

const AltaLocalidad = ({ open, provincias, provinciaInicial, onClose, onCreada }) => {
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const guardar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setGuardando(true);
    setError("");
    try {
      const res = await api.platform.geo.crearLocalidad(values);
      if (res.status === "success") {
        message.success("Localidad agregada");
        onCreada();
      } else {
        setError(res.message || "No se pudo agregar");
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Agregar localidad"
      okText="Agregar"
      onOk={guardar}
      onCancel={guardando ? undefined : onClose}
      confirmLoading={guardando}
      okButtonProps={{ style: { background: PRIMARY } }}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
      <Form form={form} layout="vertical" preserve={false} initialValues={{ id_provincia: provinciaInicial || undefined }}>
        <Form.Item label="Provincia" name="id_provincia" rules={[{ required: true, message: "Elegí la provincia" }]}>
          <Select showSearch optionFilterProp="label" options={provincias.map((p) => ({ value: p.id, label: p.nombre }))} />
        </Form.Item>
        <Form.Item label="Nombre" name="nombre" rules={[{ required: true, whitespace: true, message: "Falta el nombre" }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Departamento" name="departamento" extra="Opcional. Sirve para distinguirla de otra con el mismo nombre.">
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};
